import { CASES } from "@/content/cases";
import { Link } from "@tanstack/react-router";
import { Reveal } from "@/components/Reveal";
import { SERVICES } from "@/content/services";
import { Section } from "@/components/Section";
import { ArrowUpRight, Sparkles, TrendingUp, Globe2, Search, CheckCircle2, Bot, ShoppingBag, Link2, FileText } from "lucide-react";

export function HomeServices() {
  return (
    <>
      {/* SERVICES */}
      <Section
        eyebrow="What I do"
        title={<>Eight ways I move <span className="text-gradient">your rankings & revenue.</span></>}
        subtitle="A full-stack SEO offer — from technical foundations to AI-search visibility — built around outcomes you can put on a P&L."
      >
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-border rounded-3xl overflow-hidden border border-border">
          {SERVICES.map((s, i) => {
            const ICONS = [Search, Globe2, TrendingUp, Sparkles, Bot, ShoppingBag, Link2, FileText];
            const Icon = ICONS[i % ICONS.length];
            return (
              <Reveal key={s.slug} delay={i * 0.05}>
                <Link
                  to="/services"
                  hash={s.slug}
                  className="group relative flex h-full flex-col bg-background p-8 lg:p-10 hover:bg-card transition-colors"
                >
                  <div className="flex items-start justify-between">
                    <div className="size-12 rounded-2xl bg-primary/10 grid place-items-center text-primary group-hover:bg-primary/20 transition-colors">
                      <Icon size={22} />
                    </div>
                    <ArrowUpRight size={18} className="text-muted-foreground group-hover:text-primary group-hover:-translate-y-1 group-hover:translate-x-1 transition" />
                  </div>
                  <h3 className="mt-6 font-display text-2xl leading-snug">{s.title}</h3>
                  <p className="mt-2 text-sm text-muted-foreground">{s.tagline}</p>

                  <ul className="mt-5 space-y-1.5">
                    {s.deliverables.slice(0, 3).map((d) => (
                      <li key={d} className="flex items-start gap-2 text-xs text-muted-foreground">
                        <CheckCircle2 size={13} className="mt-0.5 text-primary shrink-0" />
                        <span>{d}</span>
                      </li>
                    ))}
                  </ul>

                  <div className="mt-auto pt-6 flex flex-wrap gap-1.5">
                    {s.outcomes.slice(0, 2).map((o) => (
                      <span
                        key={o}
                        className="inline-flex items-center rounded-full border border-border bg-card/60 px-2.5 py-1 text-[11px] text-muted-foreground"
                      >
                        {o}
                      </span>
                    ))}
                  </div>
                </Link>
              </Reveal>
            );
          })}
          <Reveal delay={SERVICES.length * 0.05}>
            <Link
              to="/contact"
              className="group relative flex h-full flex-col justify-between bg-gradient-to-br from-primary/15 via-background to-background p-8 lg:p-10 hover:from-primary/25 transition-colors"
            >
              <div className="flex items-start justify-between">
                <div className="size-12 rounded-2xl bg-primary text-primary-foreground grid place-items-center">
                  <Sparkles size={22} />
                </div>
                <ArrowUpRight size={18} className="text-primary group-hover:-translate-y-1 group-hover:translate-x-1 transition" />
              </div>
              <div className="mt-6">
                <h3 className="font-display text-2xl leading-snug">
                  Need a custom <span className="text-gradient">growth plan?</span>
                </h3>
                <p className="mt-2 text-sm text-muted-foreground">
                  Tell me about your site, your goals, and your competition. I'll reply within 24 hours with a tailored SEO roadmap.
                </p>
              </div>
              <div className="mt-6 inline-flex items-center gap-2 text-sm font-medium text-primary">
                Book a free strategy call <ArrowUpRight size={14} />
              </div>
            </Link>
          </Reveal>
        </div>

        <div className="mt-10 flex flex-wrap items-center gap-3">
          <Link
            to="/services"
            className="inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground px-5 py-3 text-sm font-medium hover:shadow-[var(--shadow-glow)] transition"
          >
            Explore all services <ArrowUpRight size={14} />
          </Link>
          <Link
            to="/free-seo-audit"
            className="inline-flex items-center gap-2 rounded-full border border-border px-5 py-3 text-sm hover:bg-card transition"
          >
            Get a free SEO audit
          </Link>
          <Link
            to="/process"
            className="inline-flex items-center gap-2 rounded-full border border-border px-5 py-3 text-sm hover:bg-card transition"
          >
            See my process
          </Link>
        </div>
      </Section>

      {/* FEATURED CASES */}
      <Section
        eyebrow="Selected work"
        title={<>Client projects, <span className="text-gradient">documented honestly.</span></>}
        subtitle="What each project involved and what was learned. Where results are not backed by records on file, the case study says so."
      >
        <div className="grid lg:grid-cols-3 gap-6">
          {CASES.slice(0, 3).map((c, i) => (
            <Reveal key={c.slug} delay={i * 0.1}>
              <Link
                to="/case-studies/$slug"
                params={{ slug: c.slug }}
                className="group block h-full rounded-3xl border border-border bg-card p-8 hover:border-primary/40 hover:shadow-[var(--shadow-glow)] transition-all"
              >
                <div className="flex items-start justify-between">
                  <span className="text-5xl">{c.cover}</span>
                  <ArrowUpRight size={20} className="text-muted-foreground group-hover:text-primary transition" />
                </div>
                <div className="mt-8 text-xs uppercase tracking-[0.18em] text-muted-foreground">
                  {c.region} · {c.industry}
                </div>
                <h3 className="mt-3 font-display text-2xl leading-snug">{c.headline}</h3>
                {c.metrics.length > 0 && (
                  <div className="mt-8 grid grid-cols-2 gap-4">
                    {c.metrics.slice(0, 2).map((m) => (
                      <div key={m.label}>
                        <div className="text-2xl font-display text-gradient">{m.value}</div>
                        <div className="text-xs text-muted-foreground mt-1">{m.label}</div>
                      </div>
                    ))}
                  </div>
                )}
              </Link>
            </Reveal>
          ))}
        </div>
        <div className="mt-12">
          <Link to="/case-studies" className="inline-flex items-center gap-2 text-sm text-primary hover:underline">
            View all case studies <ArrowUpRight size={14} />
          </Link>
        </div>
      </Section>
    </>
  );
}
